import { useCallback, useEffect, useRef, useState } from "react";

/** Sub-pixel slack so a fractional scrollLeft at the end still counts as the end. */
const END_TOLERANCE_PX = 1;

interface ScrollMetrics {
  scrollWidth: number;
  clientWidth: number;
  scrollLeft: number;
}

/** True while there is content hidden beyond the right edge. */
export function canScrollRight({
  scrollWidth,
  clientWidth,
  scrollLeft,
}: ScrollMetrics): boolean {
  return scrollWidth - clientWidth - scrollLeft > END_TOLERANCE_PX;
}

/**
 * Tracks whether a horizontally scrolling element can still scroll right.
 * Re-measures on scroll and whenever the element or its content resizes.
 */
export function useScrollFade<T extends HTMLElement>(remountKey?: unknown) {
  const ref = useRef<T | null>(null);
  const [fadeVisible, setFadeVisible] = useState(false);

  const measure = useCallback(() => {
    const element = ref.current;
    if (element) setFadeVisible(canScrollRight(element));
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    measure();
    element.addEventListener("scroll", measure, { passive: true });
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(measure);
    observer?.observe(element);
    if (element.firstElementChild) observer?.observe(element.firstElementChild);
    return () => {
      element.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [measure, remountKey]);

  return { ref, fadeVisible };
}
